import * as React from 'react'
import { Typography, Box, TextField, ConfirmationModal } from 'cs-kevlar-react'
import { ConfirmationModalProps } from 'cs-kevlar-react/lib/es/ConfirmationModal'
import { WithOptional } from 'cs-kevlar-react/lib/es/base'
import {
  makeStyles,
  createStyles,
  Theme,
} from 'cs-kevlar-react/lib/es/theming/theme'
import { CertificateRevocationStatus } from '../../api'
import { CustomerInterface } from 'contexts/CurrentCustomerContext'
import {
  useConnectorApi,
  useCurrentCustomer,
  usePageNotifications,
  useLogger,
} from 'hooks'

// mark these props as optional since we set defaults
type OptionalKeys = 'message' | 'title' | 'confirmText' | 'confirmButtonColor'

interface CertificateRevocationStatusModalProps
  extends WithOptional<ConfirmationModalProps, OptionalKeys> {
  certStatus: CertificateRevocationStatus
  connector: CustomerInterface
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    bottomGutter: {
      marginBottom: theme.spacing(8),
    },
  }),
)

export const CertificateRevocationStatusModal: React.FunctionComponent<CertificateRevocationStatusModalProps> = ({
  certStatus,
  connector,
  onCancel,
  onConfirm,
  open,
  ...rest
}) => {
  const classes = useStyles({})
  const { dispatch: dispatchNotification } = usePageNotifications()
  const { dispatch: dispatchCustomerAction } = useCurrentCustomer(
    connector.customerAgencyId!,
  )
  const { revokeCertificate, reinstateCertificate } = useConnectorApi()
  const { logError } = useLogger()

  const [confirmingStatusText, setConfirmingStatusText] = React.useState<
    string
  >('')

  const canConfirmStatus =
    confirmingStatusText.toUpperCase() ===
    (certStatus == CertificateRevocationStatus.Ok ? 'REINSTATE' : 'REVOKE')

  const onCancelClicked = () => {
    setConfirmingStatusText('')

    onCancel && onCancel()
  }

  const onConfirmClicked = () => {
    setConfirmingStatusText('')

    const update =
      certStatus == CertificateRevocationStatus.Ok
        ? reinstateCertificate
        : revokeCertificate
    update(connector.customerAgencyId!, connector.id)
      .then((response) => {
        if (!response) return

        if (response.status === 200 && response.data.isSuccess) {
          const updatedConnector: CustomerInterface = {
            ...response.data.data,
            id: response.data.data.customerInterfaceId as string,
            createdOn: new Date().toUTCString(),
            configurationOverrides: {},
            importConfiguration: null,
          }

          dispatchCustomerAction({
            type: 'EditConnector',
            payload: updatedConnector,
          })

          dispatchNotification({
            type: 'SetSuccess',
            payload: 'Certificate status updated successfully.',
          })
        } else {
          logError(
            `Failed to update certificate status with response: ${response.status.toString()}: ${
              response.statusText
            }`,
          )

          dispatchNotification({
            type: 'SetError',
            payload: 'There was an error updating the certificate status.',
          })
        }
      })
      .catch((error) => {
        logError(error)

        dispatchNotification({
          type: 'SetError',
          payload: 'There was an error updating the certificate status.',
        })
      })

    onConfirm && onConfirm()
  }

  return (
    <ConfirmationModal
      onCancel={onCancelClicked}
      onConfirm={onConfirmClicked}
      open={open}
      isConfirmDisabled={!canConfirmStatus}
      title={`Confirm Certificate ${
        certStatus == CertificateRevocationStatus.Ok
          ? 'Reinstatement'
          : 'Revocation'
      }`}
      confirmText={`Confirm ${
        certStatus == CertificateRevocationStatus.Ok
          ? 'Reinstatement'
          : 'Revocation'
      }`}
      confirmButtonColor="secondary"
      {...rest}
      message={
        <React.Fragment>
          <Typography
            className={classes.bottomGutter}
          >{`Please confirm you wish to ${
            certStatus == CertificateRevocationStatus.Ok
              ? 'reinstate'
              : 'revoke'
          } this certificate.`}</Typography>
          <Typography
            className={classes.bottomGutter}
          >{`Connector: ${connector.name}`}</Typography>
          <Box>
            <TextField
              onChange={(e) => setConfirmingStatusText(e.target.value)}
              label={`Please enter ${
                certStatus == CertificateRevocationStatus.Ok
                  ? 'REINSTATE'
                  : 'REVOKE'
              } to confirm ${
                certStatus == CertificateRevocationStatus.Ok
                  ? 'reinstatement'
                  : 'revocation'
              } of this certificate.`}
              value={confirmingStatusText}
            />
          </Box>
        </React.Fragment>
      }
    />
  )
}
